/**
 * SpringsCoverageMap — hand-coded SVG map of our Pikes Peak Region service
 * footprint. Anchors at our Woodmen Rd HQ, draws ~15mi and ~30mi service
 * rings, and plots clickable pills for every neighborhood/city we serve.
 *
 *  - Pure SVG, zero external JS, zero map API key, no images
 *  - Every pill is a real anchor link → smooth-scrolls to an in-page section
 *  - Mobile: collapses to a stacked, grouped grid (same anchors, no SVG)
 *  - A crawlable text list below the map duplicates every location for
 *    SEO/AI crawlers that skip SVG hover state
 */

import { MapPin, Crosshair } from "lucide-react";
import {
  COVERAGE_REGIONS,
  HQ,
  RING_15,
  RING_30,
  TIER_ANCHORS,
  TIER_LABELS,
  type RingTier,
} from "@/lib/springsCoverageRegions";

const TIER_ORDER: RingTier[] = ["in-city", "eastern-plains", "pikes-peak", "extended"];

const TIER_FILL: Record<RingTier, string> = {
  "in-city": "fill-brand-gold/90",
  "eastern-plains": "fill-brand-gold/70",
  "pikes-peak": "fill-brand-gold/55",
  "extended": "fill-foreground/40",
};

function handleAnchorClick(
  e: React.MouseEvent<HTMLAnchorElement>,
  anchor: string,
) {
  e.preventDefault();
  const el = document.getElementById(anchor);
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    history.replaceState(null, "", `#${anchor}`);
  }
}

export function SpringsCoverageMap() {
  const grouped = TIER_ORDER.map((tier) => ({
    tier,
    label: TIER_LABELS[tier],
    anchor: TIER_ANCHORS[tier],
    items: COVERAGE_REGIONS.filter((r) => r.tier === tier),
  }));

  return (
    <section
      id="coverage-map"
      aria-labelledby="coverage-map-heading"
      className="border-y border-border bg-gradient-to-b from-background to-muted/30 py-16 lg:py-20"
    >
      <div className="mx-auto max-w-6xl px-5 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <span className="inline-block rounded-full border border-brand-gold-deep/30 bg-brand-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-brand-gold-deep">
            Pikes Peak Region coverage map
          </span>
          <h2
            id="coverage-map-heading"
            className="mt-5 text-4xl font-extrabold text-foreground sm:text-5xl"
          >
            We serve <span className="text-gradient-gold">all of Colorado Springs</span>
            <span className="block text-2xl font-bold text-muted-foreground sm:text-3xl">
              plus a 30-mile radius around our Woodmen Rd office
            </span>
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            From Powers and I-25 to Highway 83 and Highway 24 — and out to
            Cañon City, Woodland Park, Castle Rock, Pueblo, and Limon. Click
            any city to jump to coverage details.
          </p>
        </div>

        {/* ── SVG map (desktop / tablet) ───────────────────────────────── */}
        <div className="relative mt-10 hidden md:block">
          <div className="overflow-hidden rounded-3xl border border-brand-gold/30 bg-brand-black/95 shadow-card-hover">
            <svg
              viewBox="0 0 800 600"
              role="img"
              aria-label="Colorado Springs service area map with HQ at 3210 E Woodmen Rd and a 30-mile service radius"
              className="block h-auto w-full"
            >
              <defs>
                <radialGradient id="hqGlow" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="rgb(212 175 55)" stopOpacity="0.5" />
                  <stop offset="100%" stopColor="rgb(212 175 55)" stopOpacity="0" />
                </radialGradient>
                <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                  <path
                    d="M 40 0 L 0 0 0 40"
                    fill="none"
                    stroke="rgb(255 255 255)"
                    strokeOpacity="0.04"
                    strokeWidth="1"
                  />
                </pattern>
              </defs>

              {/* Background grid */}
              <rect width="800" height="600" fill="url(#grid)" />

              {/* 30-mile ring */}
              <circle
                cx={HQ.x}
                cy={HQ.y}
                r={RING_30}
                fill="rgb(212 175 55)"
                fillOpacity="0.03"
                stroke="rgb(212 175 55)"
                strokeOpacity="0.4"
                strokeWidth="1.5"
                strokeDasharray="6 6"
              />
              <text
                x={HQ.x + RING_30 - 8}
                y={HQ.y + 4}
                textAnchor="end"
                fill="rgb(212 175 55)"
                fillOpacity="0.7"
                fontSize="11"
                fontWeight="700"
                style={{ letterSpacing: "0.1em" }}
              >
                30-MILE SERVICE RADIUS
              </text>

              {/* 15-mile ring */}
              <circle
                cx={HQ.x}
                cy={HQ.y}
                r={RING_15}
                fill="rgb(212 175 55)"
                fillOpacity="0.05"
                stroke="rgb(212 175 55)"
                strokeOpacity="0.55"
                strokeWidth="1.5"
                strokeDasharray="4 4"
              />
              <text
                x={HQ.x + RING_15 - 6}
                y={HQ.y - 6}
                textAnchor="end"
                fill="rgb(212 175 55)"
                fillOpacity="0.75"
                fontSize="10"
                fontWeight="700"
                style={{ letterSpacing: "0.1em" }}
              >
                15-MILE CORE
              </text>

              {/* HQ glow */}
              <circle cx={HQ.x} cy={HQ.y} r="60" fill="url(#hqGlow)" />
              <circle
                cx={HQ.x}
                cy={HQ.y}
                r="9"
                fill="rgb(212 175 55)"
                stroke="rgb(0 0 0)"
                strokeWidth="2"
              >
                <animate
                  attributeName="r"
                  values="9;12;9"
                  dur="2.4s"
                  repeatCount="indefinite"
                />
              </circle>
              <text
                x={HQ.x}
                y={HQ.y - 16}
                textAnchor="middle"
                fill="rgb(255 255 255)"
                fontSize="12"
                fontWeight="800"
              >
                HQ — 3210 E Woodmen Rd
              </text>

              {/* Spokes from HQ to each region */}
              {COVERAGE_REGIONS.map((r) => (
                <line
                  key={`spoke-${r.name}`}
                  x1={HQ.x}
                  y1={HQ.y}
                  x2={r.x}
                  y2={r.y}
                  stroke="rgb(212 175 55)"
                  strokeOpacity={r.tier === "extended" ? 0.18 : 0.28}
                  strokeWidth="1"
                />
              ))}

              {/* City pills */}
              {COVERAGE_REGIONS.map((r) => {
                const width = Math.max(60, r.name.length * 7 + 18);
                const x = Math.min(795, Math.max(5, r.x - width / 2));
                const y = Math.max(5, r.y - 11);
                return (
                  <g key={r.name} className="cursor-pointer group">
                    <a
                      href={`#${r.anchor}`}
                      onClick={(e) =>
                        handleAnchorClick(
                          e as unknown as React.MouseEvent<HTMLAnchorElement>,
                          r.anchor,
                        )
                      }
                    >
                      <title>{`${r.name} — ${r.direction} • ${r.drive}`}</title>
                      <rect
                        x={x}
                        y={y}
                        width={width}
                        height="22"
                        rx="11"
                        ry="11"
                        className={`${TIER_FILL[r.tier]} transition-all group-hover:fill-brand-gold`}
                        stroke="rgb(0 0 0)"
                        strokeOpacity="0.4"
                        strokeWidth="0.5"
                      />
                      <text
                        x={r.x}
                        y={r.y + 4}
                        textAnchor="middle"
                        fill="rgb(0 0 0)"
                        fontSize="11"
                        fontWeight="800"
                        className="pointer-events-none"
                      >
                        {r.name}
                      </text>
                    </a>
                  </g>
                );
              })}
            </svg>
          </div>

          {/* Legend */}
          <div className="mt-5 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs font-semibold text-muted-foreground">
            <span className="inline-flex items-center gap-2">
              <Crosshair className="h-3.5 w-3.5 text-brand-gold-deep" />
              HQ: 3210 E Woodmen Rd, 80920
            </span>
            <span className="inline-flex items-center gap-2">
              <span className="h-3 w-3 rounded-full bg-brand-gold" />
              In-city neighborhoods
            </span>
            <span className="inline-flex items-center gap-2">
              <span className="h-3 w-3 rounded-full bg-brand-gold/70" />
              Eastern plains
            </span>
            <span className="inline-flex items-center gap-2">
              <span className="h-3 w-3 rounded-full bg-brand-gold/50" />
              Pikes Peak Region
            </span>
            <span className="inline-flex items-center gap-2">
              <span className="h-3 w-3 rounded-full bg-foreground/40" />
              30-mile extended reach
            </span>
          </div>
        </div>

        {/* ── Mobile fallback: grouped pill grid (same anchors) ────────── */}
        <div className="mt-8 space-y-6 md:hidden">
          {grouped.map((g) => (
            <div
              key={g.tier}
              className="rounded-2xl border border-border bg-card p-5 shadow-card"
            >
              <a
                href={`#${g.anchor}`}
                onClick={(e) => handleAnchorClick(e, g.anchor)}
                className="flex items-center gap-2 text-sm font-extrabold uppercase tracking-[0.12em] text-brand-gold-deep"
              >
                <MapPin className="h-4 w-4" />
                {g.label}
              </a>
              <ul className="mt-3 flex flex-wrap gap-2">
                {g.items.map((r) => (
                  <li key={r.name}>
                    <a
                      href={`#${r.anchor}`}
                      onClick={(e) => handleAnchorClick(e, r.anchor)}
                      className="inline-flex items-center rounded-full border border-border bg-background px-3 py-1.5 text-xs font-bold text-foreground transition-smooth active:scale-95"
                    >
                      {r.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* ── Crawlable text list (SEO/AI fallback, all viewports) ─────── */}
        <div className="mt-10 rounded-2xl border border-border bg-card/50 p-6">
          <p className="text-sm leading-relaxed text-foreground/85">
            <strong className="text-foreground">
              Colorado Springs SEO clients we serve, by region:
            </strong>{" "}
            We rank businesses across the entire Pikes Peak Region — from our
            Woodmen Rd headquarters out to a 30-mile service radius covering
            Cañon City, Woodland Park, Castle Rock, Pueblo, and Limon.
          </p>
          <div className="mt-5 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {grouped.map((g) => (
              <div key={`text-${g.tier}`}>
                <div className="text-xs font-extrabold uppercase tracking-[0.1em] text-brand-gold-deep">
                  {g.label}
                </div>
                <ul className="mt-2 space-y-1 text-sm text-foreground/85">
                  {g.items.map((r) => (
                    <li key={`text-${r.name}`}>
                      <a
                        href={`#${r.anchor}`}
                        onClick={(e) => handleAnchorClick(e, r.anchor)}
                        className="hover:text-brand-gold-deep hover:underline"
                      >
                        {r.name}
                      </a>
                      <span className="text-muted-foreground"> · {r.drive}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
